import { useState } from 'react';
import { supabase, Miembro } from '../lib/supabase';
import { miembroService } from '../services/miembroService';
import { useToast } from '../components/ui/Toast';

export function useMiembroFichaActions(miembro: Miembro | null, onClose: () => void, onChanged?: () => void) {
  const { showToast } = useToast();
  const [generandoLink, setGenerandoLink] = useState(false);
  const [linkGenerado, setLinkGenerado] = useState<string | null>(null);
  const [linkExpiresAt, setLinkExpiresAt] = useState<string | null>(null);
  const [showConfirmRegistrar, setShowConfirmRegistrar] = useState(false);
  const [showDevolver, setShowDevolver] = useState(false);
  const [observacion, setObservacion] = useState('');
  const [devolviendo, setDevolviendo] = useState(false);
  // Tras devolver, el prop `miembro` queda stale; esta señal refleja que la ficha
  // volvió a estado de espera para actualizar la UI sin recargar.
  const [fichaDevuelta, setFichaDevuelta] = useState(false);

  const generarLink = async (): Promise<string | null> => {
    if (!miembro) return null;
    setGenerandoLink(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/invitacion/generar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token ?? ''}` },
        body: JSON.stringify({ miembro_id: miembro.id }),
      });
      const json = await res.json() as { data?: { link: string; expires_at: string }; error?: string };
      if (!res.ok) { showToast(json.error ?? 'Error al generar el link', 'error'); return null; }
      setLinkGenerado(json.data!.link);
      setLinkExpiresAt(json.data!.expires_at ?? null);
      onChanged?.();
      return json.data!.link;
    } catch { showToast('Error de conexión', 'error'); return null; }
    finally { setGenerandoLink(false); }
  };

  const handleRegistrar = async () => {
    if (!miembro) return;
    setShowConfirmRegistrar(false);
    try {
      await miembroService.registrarMiembro(miembro.id);
      showToast('Miembro registrado exitosamente', 'success');
      onChanged?.();
      onClose();
    } catch { showToast('Error al registrar el miembro', 'error'); }
  };

  const handleDevolver = async () => {
    if (!miembro || devolviendo) return;
    if (!observacion.trim()) { showToast('Escribe un comentario', 'error'); return; }
    setDevolviendo(true);
    try {
      await miembroService.devolverFicha(miembro.id, observacion);
      const newLink = await generarLink();
      setShowDevolver(false);
      setObservacion('');
      setFichaDevuelta(true);
      const msg = newLink
        ? 'Ficha devuelta. Link generado para enviar al miembro.'
        : 'Ficha devuelta. Genera un nuevo link para enviárselo al miembro.';
      showToast(msg, 'success');
    } catch { showToast('Error al devolver la ficha', 'error'); }
    finally { setDevolviendo(false); }
  };

  return {
    generandoLink,
    linkGenerado,
    linkExpiresAt,
    showConfirmRegistrar, setShowConfirmRegistrar,
    showDevolver, setShowDevolver,
    observacion, setObservacion,
    devolviendo,
    fichaDevuelta,
    generarLink,
    handleRegistrar,
    handleDevolver,
  };
}
